import { AxiosError } from 'axios';
import { ApiErrors } from '@types';

import {
  errorMessages,
  GENERIC_ERROR_MESSAGE,
} from 'shared/constants/errorMessages';
import { Product } from '../services/types';
import {
  exceptionFunctionalities,
  VerificationIcons,
} from '../constants/functionalities';

export const getPeriod = (isAnnual: boolean) =>
  isAnnual ? 'annual' : 'monthly';

export const formatPeriod = (period: string, frequency: number): string => {
  if (frequency <= 0) return '';

  const totalMonths = period === 'annual' ? frequency * 12 : frequency;

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  const parts: string[] = [];
  if (years > 0) {
    const yearLabel = years === 1 ? 'ano' : 'anos';
    parts.push(`${years} ${yearLabel}`);
  }

  if (months > 0) {
    const monthLabel = months === 1 ? 'mês' : 'meses';
    parts.push(`${months} ${monthLabel}`);
  }

  return parts.join(' e ');
};

export const splitValue = (value: string) => {
  const fullValue = Number(value).toFixed(2);
  const [intergPart, decimalPart] = fullValue.split('.');

  return {
    integer: intergPart,
    decimal: decimalPart,
  };
};

const MONTHS_IN_ONE_YEAR = 12;

export const getAnnualDiscount = (plan: Product) => {
  const valueMonthlyByOneYear = Number(plan.monthly_price) * MONTHS_IN_ONE_YEAR;
  const discountByYear = valueMonthlyByOneYear - Number(plan.annual_price);

  return discountByYear.toFixed(2);
};

interface PlanDetailsData {
  period: string;
  name: string;
  fullValue: string;
  valueInteger: string;
  valueDecimal: string;
  fullDiscountValue?: string;
  discountValueInteger?: string;
  discountValueDecimal?: string;
  frequencyNumber?: number;
  percentageValue?: string;
  dateExpiration?: string;
}

const getMonthlyValueOfAnnual = (value: string, isAnnual: boolean) =>
  isAnnual ? Number(value) / MONTHS_IN_ONE_YEAR : Number(value);

const formatPriceParts = (value: number) => {
  const valueInteger = Math.floor(value);
  const valueDecimal = Math.round((value - valueInteger) * 100);

  return {
    valueInteger: valueInteger.toString(),
    valueDecimal: valueDecimal.toFixed(0).padStart(2, '0'),
  };
};

export const getPlanDetails = (
  plan: Product,
  isAnnual: boolean,
): PlanDetailsData => {
  const { name, promotion, monthly_price, annual_price } = plan;

  const period = getPeriod(isAnnual);
  const sufix = 'mês';

  const promotionByRecurrence = promotion?.frequencies[period];
  const basePrice = isAnnual ? annual_price : monthly_price;

  const monthlyValue = getMonthlyValueOfAnnual(basePrice, isAnnual);

  const { valueInteger, valueDecimal } = formatPriceParts(monthlyValue);

  const planDetails: PlanDetailsData = {
    period: sufix,
    name,
    fullValue: basePrice,
    valueInteger: splitValue(valueInteger.toString()).integer,
    valueDecimal,
  };

  if (!promotionByRecurrence) return planDetails;

  const promotionValuePerMonth = getMonthlyValueOfAnnual(
    promotionByRecurrence.value,
    isAnnual,
  );

  const {
    valueInteger: promotionPriceInteger,
    valueDecimal: promotionPriceDecimal,
  } = formatPriceParts(promotionValuePerMonth);
  return {
    ...planDetails,
    fullDiscountValue: promotionByRecurrence.value,
    discountValueInteger: splitValue(promotionPriceInteger.toString()).integer,
    discountValueDecimal: promotionPriceDecimal,
    frequencyNumber: promotionByRecurrence.frequencyNumber,
    percentageValue: splitValue(promotionByRecurrence.percentage).integer,
    dateExpiration: formatPeriod(period, promotionByRecurrence.frequencyNumber),
  };
};

export const getCheckIconException = (
  planSlug: string,
  functionalitySlug: string,
) => {
  const content = exceptionFunctionalities[planSlug][functionalitySlug];
  if (content) {
    return content?.icon;
  }
  return null;
};

export const getVerificationIcon = (
  productsFuncionalityIsIncluded: string[],
  planSlug: string,
  functionalitySlug: string,
) => {
  const exception = getCheckIconException(planSlug, functionalitySlug);
  if (exception) {
    return exception;
  }
  return productsFuncionalityIsIncluded.includes(planSlug)
    ? VerificationIcons.checked
    : VerificationIcons.unchecked;
};

export const getResponseErrors = (error: AxiosError) =>
  (error.response?.data as { errors?: ApiErrors[] })?.errors ?? [];

export const getErrorsMessage = (errors: ApiErrors[]) =>
  errors.map((error) => errorMessages[error.code] || GENERIC_ERROR_MESSAGE) ||
  [];

export const customizeErrorMessage = (status: number): string => {
  if (status === 401) {
    return 'Cupom não autorizado. Verifique o código inserido.';
  }
  if (status === 404) {
    return 'Cupom não encontrado. Verifique o código inserido.';
  }
  return 'Cupom inválido. Verifique o código inserido.';
};

export const saveIsVoucherApplied = (isApplied: string) => {
  localStorage.setItem('isVoucherApplied', isApplied);
};

export const loadIsVoucherApplied = (): string | null =>
  localStorage.getItem('isVoucherApplied');
